// src/pages/Exam/QuestionCard.jsx
import React, { useRef } from "react";
import OptionCard from "./OptionCard";
import { InputTextarea } from "primereact/inputtextarea";
import { useExam } from "../../contexts/ExamContext";
import GeneralUtils from "../../utils/GeneralUtils";

const QuestionCard = ({ questionOverride = null, interactive = true}) => {
    const {
        exam,
        questions = [],
        studentAnswers = [],
        currentQuestionIndex,
        questionHtmlOverrides,
        setQuestionHtml,
        answers,
        setOpenAnswer,
        clearAnswer
    } = useExam();

    // questions already come ordered for the student
    const question = questionOverride ?? questions?.[currentQuestionIndex];
    const displayNumber = (() => {
        if (!question) return "";
        // find related studentAnswer to read objectIndex
        const sa = studentAnswers.find(sa => sa?.question?.id === question.id);
        return sa?.objectIndex ?? (questionOverride
            ? (questions.findIndex(q => q?.id === questionOverride?.id) + 1)
            : (currentQuestionIndex + 1));
    })();

    const qRef = useRef(null);
    const qHtml = questionHtmlOverrides?.[question?.id] ?? question?.question;

    const qType = question?.questionType || question?.type;
    const isMultipleChoice = qType === "MultipleChoice";
    const isTrueFalse = qType === "TrueFalse";
    const isOpenEnded = qType === "OpenEnded";

    const applyHighlight = (container, save) => {
        if (!interactive) return;
        const sel = window.getSelection?.();
        if (!sel || sel.isCollapsed) return;
        if (!container.current) return;
        if (!container.current.contains(sel.anchorNode) || !container.current.contains(sel.focusNode)) return;

        const range = sel.getRangeAt(0);
        try {
            const mark = document.createElement("mark");
            mark.className = "user-highlight";
            range.surroundContents(mark);
            sel.removeAllRanges();
        } catch {
            const frag = range.cloneContents();
            const mark = document.createElement("mark");
            mark.className = "user-highlight";
            mark.appendChild(frag);
            range.deleteContents();
            range.insertNode(mark);
            sel.removeAllRanges();
        }
        save(container.current.innerHTML);
    };

    const isUserMark = (n) =>
        n && n.nodeType === 1 && n.tagName === "MARK" && n.classList.contains("user-highlight");

    const unwrapMark = (el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        return parent;
    };

    const removeContiguousGroup = (markEl) => {
        const group = [markEl];
        let n = markEl.previousSibling;
        while (isUserMark(n)) {
            group.unshift(n);
            n = n.previousSibling;
        }
        n = markEl.nextSibling;
        while (isUserMark(n)) {
            group.push(n);
            n = n.nextSibling;
        }
        group.forEach(unwrapMark);
    };

    const handleRightClickHighlightRemoval = (e) => {
        if (!interactive) return;
        const container = qRef.current;
        if (!container) return;
        let el = e.target;
        while (el && el !== container && !isUserMark(el)) el = el.parentNode;
        if (isUserMark(el)) {
            e.preventDefault();
            removeContiguousGroup(el);
            if (question?.id) setQuestionHtml(question.id, container.innerHTML);
        }
    };

    const openValue = answers?.[question?.id] ?? "";

    const onOpenChange = (e) => {
        if (!interactive) return;
        const val = e.target.value;
        if (!question?.id) return;
        if (val.trim().length === 0) {
            clearAnswer(question.id);
        } else {
            // direct set (no toggle semantics)
            setOpenAnswer(question.id, val);
        }
    };

    return (
        <div className="flex flex-column p-4 shadow-2 border-round-xl surface-card bg-white" style={{ width: "100%", minHeight: "400px" }}>
            <style>{`.user-highlight { background: #fde68a; color: #000; padding: 0 .05rem; }`}</style>

            {/* Question */}
            <div className="flex gap-2 align-items-center text-900 mb-2">
                <span className="font-bold text-lg">{displayNumber}.</span>
                <div
                    ref={qRef}
                    className="text-lg"
                    onMouseUp={() => {
                        if (!question?.id) return;
                        applyHighlight(qRef, (html) => setQuestionHtml(question.id, html));
                    }}
                    onContextMenu={handleRightClickHighlightRemoval}
                    dangerouslySetInnerHTML={{ __html: qHtml }}
                />
            </div>

            {/* OpenEnded answer area */}
            {isOpenEnded && (
                <div className="mt-3">
                    <InputTextarea autoResize
                                   value={openValue}
                                   onChange={onOpenChange}
                                   rows={5}
                                   cols={30}
                                   // readOnly={!interactive}
                                   placeholder="Açıklamanızı yazın..."/>
                </div>
            )}

            {/* Options */}
            {!isOpenEnded && (
                <div className="flex flex-column gap-3">
                    {question?.optionList
                        ?.slice()
                        ?.sort(GeneralUtils.compareIndex)
                        ?.map((option, idx) => (
                            <OptionCard
                                key={option.id}
                                questionId={question.id}
                                option={option}
                                optionIndex={idx}
                                showLetters={isMultipleChoice}
                                showRadio={isTrueFalse}
                                // interactive={interactive}
                            />
                        ))}
                </div>
            )}
        </div>
    );
};

export default QuestionCard;